import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, SaveIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { generatePath, useNavigate } from "react-router-dom";

import { Button, PageBreadcrumb } from "@/modules/common/components";
import { AdminUserRole, AdminUserStatus } from "@/modules/common/types/Users.enum";
import { digitsOnly } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import AdminUserFormFields from "../../components/AdminUserFormFields";
import useCreateUser from "../../hooks/useCreateUser";
import { AdminUserFormData, adminUserSchema } from "../../schemas";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import UserCreatedModal from "./components/UserCreatedModal";

const UserAdd = () => {
  const navigate = useNavigate();
  const navigateBack = useNavigateBack(ROUTES.USERS.LIST);
  const { canUserSee } = useSessionStore();
  const createUser = useCreateUser();

  const [createdUser, setCreatedUser] = useState<{ id: string; email: string } | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminUserFormData>({
    resolver: zodResolver(adminUserSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      role: "",
      status: String(AdminUserStatus.ACTIVE),
    },
  });

  const onSubmit = async (values: AdminUserFormData) => {
    const data = await createUser.mutateAsync({
      name: values.name,
      email: values.email,
      phoneNumber: values.phoneNumber ? digitsOnly(values.phoneNumber) : undefined,
      role: Number(values.role) as AdminUserRole,
      status: Number(values.status) as AdminUserStatus,
    });

    setCreatedUser({ id: data.id, email: data.email });
  };

  const handleCloseSuccessModal = () => {
    if (!createdUser) return;
    navigate(generatePath(ROUTES.USERS.DETAILS, { id: createdUser.id }));
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Usuários Administrativos", to: ROUTES.USERS.LIST }, { label: "Cadastrar Usuário" }]} />

      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col">
            <p className="text-[22px] font-bold text-ui-primary">Cadastrar Usuário</p>
            <p className="text-[14px] text-ui-text-muted">Preencha os dados para cadastrar um novo usuário administrativo.</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outlined"
              label="Voltar"
              icon={<ArrowLeft />}
              className="w-full sm:w-auto"
              onClick={navigateBack}
            />
            {canUserSee(AdminUserRole.ADMIN) && (
              <Button
                type="submit"
                label="Salvar Usuário"
                icon={<SaveIcon />}
                loading={createUser.isPending}
                disabled={createUser.isPending}
                className="w-full sm:w-auto"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
          <div className="flex flex-col">
            <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
            <p className="text-[12px] text-ui-text-muted">Informações cadastrais do usuário.</p>
          </div>

          <AdminUserFormFields register={register} control={control} errors={errors} />
        </div>
      </form>

      <UserCreatedModal open={!!createdUser} userEmail={createdUser?.email} onClose={handleCloseSuccessModal} />
    </div>
  );
};

export default UserAdd;
